import React, { useEffect, useRef, useState } from 'react';
import { Accordion } from '@mantine/core';
import { registerOverlayPortal, createUsePuck } from '@puckeditor/core';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import { textRoleCss } from '../../../puck/fields/TextRolesField';
import ProposalBoxAlign from '../../proposal/ProposalBoxAlign/ProposalBoxAlign';
import {
  notifyExclusivePanelOpened,
  subscribeAccordionPanels,
  subscribePanelPreviewToggle,
} from '../../../utils/accordionPanelsRegistry';
import { accordionPanelStyles } from '../../../../shared/boxes/accordionPanel/accordionPanelRender';

const usePuckApi = createUsePuck();

const PANEL_VALUE = 'panel';

/**
 * Éditeur : l'overlay Puck mange les clics du header — portal + stop click
 * (phase bubble : le bouton Mantine garde son toggle) + sélection du bloc.
 */
function useEditableAccordionHeader(controlRef, blockId, enabled) {
  const dispatch = usePuckApi((s) => s.dispatch);
  const getSelectorForId = usePuckApi((s) => s.getSelectorForId);

  useEffect(() => {
    const el = controlRef.current;
    if (!enabled || !el) return undefined;

    const onClick = (e) => {
      e.stopPropagation();
      const itemSelector = blockId ? getSelectorForId(blockId) : null;
      if (itemSelector) dispatch({ type: 'setUi', ui: { itemSelector } });
    };
    el.addEventListener('click', onClick, false);
    const cleanupPortal = registerOverlayPortal(el, { disableDragOnFocus: true });

    return () => {
      el.removeEventListener('click', onClick, false);
      cleanupPortal?.();
    };
  }, [controlRef, blockId, enabled, dispatch, getSelectorForId]);
}

function EditorHeaderPortal({ controlRef, blockId }) {
  useEditableAccordionHeader(controlRef, blockId, true);
  return null;
}

/**
 * Bloc Puck — Volet : question dépliable + réponse rich text.
 * Ouverture contrôlée ; « une seule ouverte à la fois » coordonnée entre
 * volets via accordionPanelsRegistry.
 */
const AccordionPanel = ({
  id,
  question = '',
  answer = '',
  /** Fond du header — vide = défaut. */
  panelColor = '',
  /* Clé de l'échelle partagée ; 'none' pour un volet déjà en base. */
  shadow = 'none',
  answerBackground = '',
  /** Bordures configurables — cf. utils/borderStyles (défaut : 4 côtés, arrondi). */
  border = null,
  openBehavior = 'exclusive',
  /** Position du volet dans sa colonne (comme l'Image). */
  alignHorizontal = 'center',
  alignVertical = 'center',
  /** Aperçus du thème : ouvert d'office, la réponse montre son style. Jamais enregistré. */
  previewOpened = false,
  /** Style par défaut des textes, par rôle (`question`, `answer`). */
  textStyles = null,
  puck,
}) => {
  const isEditing = !!puck?.isEditing;
  // Éditeur : ouvert d'office (la réponse doit être visible/éditable).
  const [opened, setOpened] = useState(isEditing || previewOpened);
  const controlRef = useRef(null);

  // Coordination « une seule ouverte » : viewer uniquement — en édition tous
  // les volets restent ouvrables librement.
  useEffect(() => {
    if (isEditing) return undefined;
    return subscribeAccordionPanels((openedPanelId) => {
      if (openedPanelId !== id && openBehavior === 'exclusive') setOpened(false);
    });
  }, [id, openBehavior, isEditing]);

  // Éditeur : bouton « Ouvrir / fermer » de la sidebar (aperçu de l'état fermé).
  useEffect(() => {
    if (!isEditing) return undefined;
    return subscribePanelPreviewToggle((panelId) => {
      if (panelId === id) setOpened((o) => !o);
    });
  }, [id, isEditing]);

  const handleChange = (value) => {
    const next = value === PANEL_VALUE;
    setOpened(next);
    if (next && !isEditing && openBehavior === 'exclusive') notifyExclusivePanelOpened(id);
  };

  // Cadre, fond d'en-tête et fond de réponse : mêmes valeurs que la box du
  // document (shared/boxes/accordionPanel/accordionPanelRender).
  const styles = accordionPanelStyles(
    {
      panelColor, answerBackground, border, shadow,
    },
    { opened },
  );

  return (
    <ProposalBoxAlign horizontal={alignHorizontal} vertical={alignVertical} innerMode="full">
      <Accordion
        value={opened ? PANEL_VALUE : null}
        onChange={handleChange}
        styles={{
          // Chrome Mantine remplacé par les bordures configurables. Pas
          // d'overflow:hidden (il clippait le bubble menu du texte riche) :
          // l'arrondi du fond du header est porté par le Control lui-même.
          item: styles.frame,
          // Fond de la réponse : tout le panel (pleine largeur, jusqu'en bas),
          // coins bas alignés sur l'arrondi de l'item.
          panel: styles.answer,
          // Édition : le label Mantine a overflow:hidden (ellipsis) — il
          // DÉCOUPAIT la toolbar de l'éditeur de question rendue dedans.
          ...(isEditing ? { label: { overflow: 'visible' } } : {}),
        }}
      >
        {/* Composant feuille : usePuck interdit hors <Puck> (viewer). */}
        {isEditing && <EditorHeaderPortal controlRef={controlRef} blockId={id} />}
        <Accordion.Item value={PANEL_VALUE}>
          {/* Édition : header élevé en permanence — la toolbar de la question
              (flippée sous le header) doit passer au-dessus du panel réponse
              qui suit dans le DOM. Le texte qui a le focus, lui, monte plus
              haut encore (200, cf. ProposalRichTextBubbleEditor.module.css) :
              sans ça cet en-tête coupait la toolbar de la réponse quand elle
              bascule vers le haut. */}
          <div
            ref={controlRef}
            style={isEditing ? { position: 'relative', zIndex: 120 } : undefined}
          >
            <Accordion.Control
              /* Édition : <div> au lieu de <button> — un contenteditable dans
                 un bouton natif perd caret/sélection. Les clics dans l'éditeur
                 sont stoppés (usePuckEditableSurface) : le toggle reste sur le
                 chevron et le padding du header. */
              component={isEditing ? 'div' : undefined}
              // Suit l'arrondi de l'item : angles bas droits quand ouvert.
              style={styles.header}
            >
              {isEditing ? (
                <PuckInlineRichText
                  id={id}
                  puck={puck}
                  html={question}
                  contentProp="question"
                  style={textRoleCss(textStyles, 'question')}
                  minHeight={24}
                  placeholder="Question"
                />
              ) : (
                /* Rien plutôt qu'un libellé de repli : « Question » s'affichait
                   tel quel sur le site publié dès que le texte était vide. */
                <PuckInlineRichText
                  id={id}
                  puck={puck}
                  html={question}
                  contentProp="question"
                  style={textRoleCss(textStyles, 'question')}
                />
              )}
            </Accordion.Control>
          </div>
          <Accordion.Panel>
            <PuckInlineRichText
              id={id}
              puck={puck}
              html={answer}
              contentProp="answer"
              style={textRoleCss(textStyles, 'answer')}
              minHeight={isEditing ? 64 : undefined}
              placeholder="Réponse"
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </ProposalBoxAlign>
  );
};

export default AccordionPanel;
